import React from "react";
import { AlertTriangle, Paperclip, UserRound } from "lucide-react";
import { fmtDateTime } from "@/lib/residentApi";
import { Empty } from "@/components/nds/windows/resident/ui";

const TABS = [["open", "Open"], ["closed", "Closed"], ["all", "All"]];

export const MemoFilters = ({ f, setF, side }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="pmemo-filters">
    {TABS.map(([k, l]) => (
      <button key={k} type="button" onClick={() => setF({ ...f, status: k })} data-testid={`pmemo-tab-${k}`} aria-pressed={f.status === k}
        className={`px-2.5 py-1 rounded-full border font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${f.status === k ? "border-nex-gold/60 text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>{l}</button>
    ))}
    {side === "staff" && (
      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="pmemo-kind"
        className="px-2 py-1 bg-black/40 border border-white/10 text-nex-silver text-[11px] rounded-sm">
        <option value="">Partners + staff</option><option value="partner">With partners</option><option value="internal">Staff only</option>
      </select>
    )}
    <label className="text-[11px] text-nex-muted inline-flex items-center gap-1.5 ml-1"><input type="checkbox" checked={f.unread} onChange={(e) => setF({ ...f, unread: e.target.checked })} data-testid="pmemo-unread-only" /> Unread only</label>
    <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search…" data-testid="pmemo-search"
      className="ml-auto w-40 px-2 py-1 bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-xs rounded-sm" />
  </div>
);

export const whoLine = (m, side) => {
  const to = m.to_staff?.name;
  if (m.kind === "internal") return `Staff · ${m.author?.name} → ${to}`;
  if (side === "partner") return m.author?.kind === "partner" ? `To ${to || "any TEA staff"}` : `From ${m.author?.name}`;
  return `${m.partner_name}${m.author?.kind === "partner" ? ` → ${to || "any TEA staff"}` : ""}`;
};

const files = (m) => (m.attachments?.length || 0) + (m.replies || []).reduce((n, r) => n + (r.attachments?.length || 0), 0);

export default function MemoList({ memos, side, onOpen }) {
  if (!memos.length) return <Empty testId="pmemo-empty">No memos here yet.</Empty>;
  return (
    <ul className="divide-y divide-white/5 border border-white/10 rounded-sm" data-testid="pmemo-list">
      {memos.map((m) => {
        const unread = m.unread;
        return (
          <li key={m.id}>
            <button type="button" onClick={() => onOpen(m)} data-testid={`pmemo-row-${m.id}`}
              className="w-full text-left px-3 py-2.5 flex items-start gap-3 hover:bg-white/[0.03] transition-colors">
              <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${unread ? "bg-nex-gold" : "bg-transparent"}`} data-testid={unread ? `pmemo-unread-${m.id}` : undefined} />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  {m.priority === "urgent" && <AlertTriangle className="h-3.5 w-3.5 text-red-300 shrink-0" data-testid={`pmemo-urgent-${m.id}`} />}
                  <span className={`truncate text-sm ${unread ? "text-nex-silver font-semibold" : "text-nex-silver/80"}`}>{m.subject}</span>
                  {m.status !== "open" && <span className="font-mono text-[9px] uppercase text-nex-muted">closed</span>}
                </span>
                <span className="flex flex-wrap items-center gap-x-3 text-[11px] text-nex-muted">
                  <span data-testid={`pmemo-who-${m.id}`}>{whoLine(m, side)}</span>
                  {m.resident && <span className="inline-flex items-center gap-1 text-sky-300/80"><UserRound className="h-3 w-3" />{m.resident.name}</span>}
                  <span>{(m.replies || []).length} repl{(m.replies || []).length === 1 ? "y" : "ies"}</span>
                  {files(m) > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip className="h-3 w-3" />{files(m)}</span>}
                </span>
              </span>
              <span className="text-[10px] text-nex-muted whitespace-nowrap">{fmtDateTime(m.last_activity_at)}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
